/**
 * CookiePolicy component displays the company's cookie and storage technology policies
 * @component
 * @description Provides detailed information about:
 * - Types of cookies and storage technologies used
 * - Data retention policies for each product
 * - User controls and preferences
 * - Technical implementation details
 * @returns {JSX.Element} The rendered CookiePolicy component
 */

import React from 'react';
import './Policy.css';

const CookiePolicy = () => {
  return (
    <div className="policy-container">
      <h1>Cookie Policy</h1>
      <p className="last-updated">Last Updated: May 30, 2025</p>

      <section>
        <h2>1. Introduction</h2>
        <p>
          At TearFace, we use cookies and similar technologies to enhance your experience with our website and services. This Cookie Policy explains how we use these technologies across our main website and products (Accessibility Widget, Chat Support Widget, and Digital Portfolio Services).
        </p>
      </section>

      <section>
        <h2>2. What Are Cookies and Storage Technologies?</h2>
        <p>
          We use various storage technologies:
        </p>
        <ul>
          <li><strong>Cookies:</strong> Small text files stored on your device when you visit our website</li>
          <li><strong>Local Storage:</strong> Web browser feature that stores data with no expiration date</li>
          <li><strong>Session Storage:</strong> Similar to local storage but clears when the browser session ends</li>
          <li><strong>IndexedDB:</strong> Low-level API for client-side storage of structured data</li>
        </ul>
      </section>

      <section>
        <h2>3. How We Use Storage Technologies</h2>
        
        <h3>3.1 Essential Storage</h3>
        <ul>
          <li>Authentication tokens and session management</li>
          <li>Security features and fraud prevention</li>
          <li>Basic website functionality</li>
          <li>Contact form and waitlist submission validation</li>
          <li>Anti-spam and security measures for submissions</li>
          <li>Temporary storage of form data during transmission</li>
        </ul>

        <h3>3.2 Accessibility Widget Storage</h3>
        <ul>
          <li>Essential Feature Storage:
            <ul>
              <li>User accessibility preferences (localStorage)</li>
              <li>Active profile selection (localStorage)</li>
              <li>Feature state management (sessionStorage)</li>
              <li>Performance optimization caches (localStorage)</li>
            </ul>
          </li>
          <li>Visual Preferences:
            <ul>
              <li>Color schemes and contrast settings (localStorage)</li>
              <li>Font size and family preferences (localStorage)</li>
              <li>Cursor and focus indicators (localStorage)</li>
              <li>Animation and motion preferences (localStorage)</li>
            </ul>
          </li>
          <li>Navigation Settings:
            <ul>
              <li>Keyboard shortcut preferences (localStorage)</li>
              <li>Reading guide positions (sessionStorage)</li>
              <li>Page structure data (sessionStorage)</li>
              <li>Navigation history (sessionStorage)</li>
            </ul>
          </li>
          <li>Feature Usage Data:
            <ul>
              <li>Feature interaction logs (IndexedDB)</li>
              <li>Performance metrics (localStorage)</li>
              <li>Error tracking data (sessionStorage)</li>
              <li>Usage statistics (localStorage)</li>
            </ul>
          </li>
        </ul>

        <h3>Browser Extension Storage (Coming Soon)</h3>
        <ul>
          <li>Extension Configuration:
            <ul>
              <li>Global preferences (chrome.storage.sync)</li>
              <li>Site-specific settings (chrome.storage.local)</li>
              <li>Disabled domains list (chrome.storage.local)</li>
              <li>User profiles (chrome.storage.sync)</li>
            </ul>
          </li>
          <li>Sync Data:
            <ul>
              <li>Cross-device preferences (cloud storage)</li>
              <li>Account settings (chrome.storage.sync)</li>
              <li>Custom profiles (chrome.storage.sync)</li>
              <li>Usage history (chrome.storage.local)</li>
            </ul>
          </li>
        </ul>

        <h3>Data Retention and Clearing</h3>
        <ul>
          <li>Local Storage:
            <ul>
              <li>Persists until manually cleared</li>
              <li>Cleared by browser "Clear Site Data"</li>
              <li>Not affected by session end</li>
              <li>May be cleared by privacy tools</li>
            </ul>
          </li>
          <li>Session Storage:
            <ul>
              <li>Cleared when browser session ends</li>
              <li>Separate for each tab/window</li>
              <li>Cleared by page refresh</li>
              <li>Not shared between sessions</li>
            </ul>
          </li>
          <li>Extension Storage:
            <ul>
              <li>Sync storage persists until account removal</li>
              <li>Local storage cleared on extension uninstall</li>
              <li>Manual clearing available in extension</li>
              <li>Backup options provided for settings</li>
            </ul>
          </li>
        </ul>

        <h3>Storage Security</h3>
        <ul>
          <li>All stored data is encrypted at rest</li>
          <li>Sensitive data never stored in plain text</li>
          <li>Regular security audits of storage usage</li>
          <li>Automatic data sanitization on storage</li>
          <li>Storage access restricted to widget domain</li>
          <li>Extension storage isolated from websites</li>
        </ul>

        <h3>3.3 Chat Support Widget Storage</h3>
        <ul>
          <li>Chat session information</li>
          <li>User conversation history (if enabled)</li>
          <li>Widget preferences and settings</li>
        </ul>

        <h3>3.4 Portfolio Services Storage</h3>
        <ul>
          <li>Session tokens for portfolio management</li>
          <li>User interface preferences</li>
          <li>Portfolio draft autosave data</li>
          <li>Hero animation completion status</li>
        </ul>
      </section>

      <section>
        <h2>4. Data Retention</h2>
        <p>Storage duration varies by type:</p>
        <ul>
          <li><strong>Session Storage:</strong> Cleared when browser session ends</li>
          <li><strong>Local Storage:</strong> Persists until manually cleared</li>
          <li><strong>Cookies:</strong> Varies by purpose (session to 1 year maximum)</li>
          <li><strong>Authentication Tokens:</strong> Valid for the duration of your session or until logout</li>
        </ul>
      </section>

      <section>
        <h2>5. Third-Party Services</h2>
        <p>
          We may integrate with third-party services that use their own cookies and storage technologies:
        </p>
        <ul>
          <li>Payment processors for portfolio services</li>
          <li>Content delivery networks (CDNs)</li>
          <li>Security and fraud prevention services</li>
        </ul>
        <p>
          We do not sell or share your stored data with third parties for marketing purposes.
        </p>
      </section>

      <section>
        <h2>6. Managing Storage Preferences</h2>
        <p>You can manage storage technologies through:</p>
        <ul>
          <li>Browser settings to clear or block cookies and site data</li>
          <li>Our products' built-in settings panels</li>
          <li>Incognito/Private browsing mode</li>
        </ul>
        <p>
          Note: Blocking essential storage may affect the functionality of our services.
        </p>
      </section>

      <section>
        <h2>7. Updates to This Policy</h2>
        <p>
          We may update this Cookie Policy to reflect changes in our practices or for other operational, legal, or regulatory reasons. The date at the top of this policy indicates when it was last updated.
        </p>
      </section>

      <section>
        <h2>8. Contact Information</h2>
        <p>
          For questions about our use of cookies and storage technologies, please contact:
        </p>
        <p>Email: hellotearface@gmail.com</p>
      </section>

      <section>
        <h2>9. No Waiver and Severability</h2>
        <p>
          If any part of this Cookie Policy is found to be invalid, unenforceable, or incomplete, the remaining provisions will remain in full force and effect. Our failure to enforce any right or provision of this policy does not constitute a waiver of that right or provision. No omission, error, or imperfection in this document will create any liability or waive any limitation of liability, and you agree that our liability remains limited as described herein regardless of any such issue.
        </p>
      </section>
    </div>
  );
};

export default CookiePolicy;
